import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DealActivity, ActivityType } from '@/types/dealTracker';
import { toast } from 'sonner';

export const useDealActivities = (leadId: string) => {
  const queryClient = useQueryClient();

  const { data: activities = [], isLoading } = useQuery({
    queryKey: ['deal-activities', leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('deal_activities')
        .select('*')
        .eq('lead_id', leadId)
        .order('activity_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as DealActivity[];
    },
    enabled: !!leadId,
  });

  const createActivity = useMutation({
    mutationFn: async (activity: {
      activity_type: ActivityType;
      title: string;
      activity_date: string;
      activity_time?: string;
      duration_minutes?: number;
      outcome?: string;
      notes?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await (supabase as any)
        .from('deal_activities')
        .insert({ ...activity, lead_id: leadId, seller_id: user.id });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-activities', leadId] });
      toast.success('Activity logged');
    },
    onError: () => toast.error('Failed to log activity'),
  });

  const updateActivity = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<{
      activity_type: ActivityType;
      title: string;
      activity_date: string;
      activity_time?: string;
      duration_minutes?: number;
      outcome?: string;
      notes?: string;
    }>) => {
      const { error } = await (supabase as any)
        .from('deal_activities')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-activities', leadId] });
      toast.success('Activity updated');
    },
    onError: () => toast.error('Failed to update activity'),
  });

  const deleteActivity = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('deal_activities')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-activities', leadId] });
      toast.success('Activity deleted');
    },
    onError: () => toast.error('Failed to delete activity'),
  });

  return { activities, isLoading, createActivity, updateActivity, deleteActivity };
};
